import { detectContentLanguage } from "@repo/shared/api";
import {
  type UiLocale,
  localePrefix,
  splitLocalePrefix,
  withLocalePrefix,
} from "@repo/shared/i18n";

import { ROUTE_BASE } from "./game";

/** Where the stored language preference lives; index.tsx wires it into shared storage. */
export const CONTENT_LANGUAGE_STORAGE_KEY = "contentLanguage";

/*
  The locale prefix sits after the edition route base: /fc27/de/sbc. Resolved
  once from the URL the page loaded with; the router basename never changes
  while the app is mounted, so a language switch is a full page load.
*/
const stripRouteBase = (pathname: string) =>
  ROUTE_BASE && pathname.startsWith(ROUTE_BASE)
    ? pathname.slice(ROUTE_BASE.length) || "/"
    : pathname;

/*
  The URL is the only source of the page's language. A prefixed URL never
  redirects an anonymous visitor: a shared /de link must render German for
  everyone. Before anything renders (index.tsx), a logged-in user is sent to
  the language their profile mirrored into storage, and an anonymous visitor
  on an unprefixed URL is sent to their stored pick, or failing that to their
  browser's language (DEV-3831).
*/
const bootLocale: UiLocale =
  typeof window === "undefined"
    ? "en-US"
    : splitLocalePrefix(stripRouteBase(window.location.pathname)).locale;

/** What auto-detection picks from the browser's languages; null when we have none of them. */
export const detectBrowserContentLanguage = () =>
  detectContentLanguage(navigator.languages ?? [navigator.language]);

/** The locale the URL carries; null when unprefixed. */
export const URL_LOCALE: UiLocale | null =
  bootLocale === "en-US" ? null : bootLocale;

export const LOCALE_BASE = URL_LOCALE ? localePrefix(URL_LOCALE) : "";

/** Edition route base plus the URL's locale prefix. Empty for the root English deploy. */
export const ROUTER_BASENAME = `${ROUTE_BASE}${LOCALE_BASE}`;

/** The current page's URL under `locale`, query and hash kept. */
export const currentPathUnderLocale = (locale: UiLocale): string => {
  const { pathname, search, hash } = window.location;
  const { rest } = splitLocalePrefix(stripRouteBase(pathname));
  return `${ROUTE_BASE}${withLocalePrefix(rest, locale)}${search}${hash}`;
};
